import { useState } from "react";
import type { CoachingClip } from "@/lib/types";
import { formatPlayhead } from "@/features/playback/useVideoPlayhead";
import { FeedbackPlayerModal } from "@/features/playback/FeedbackPlayerModal";
import { FeedbackThumb } from "./components/FeedbackCardBits";

/**
 * `/feedback` Clips tab (M7) — the viewer's coaching clips as
 * compact cards. Ports legacy `renderFeedbackClips`
 * (`js/coaching/feedback.js`).
 *
 * Same compact card shape as Notes — thumb + "Clip · {category}"
 * pill + title + meta. Clicking opens the M5
 * `<FeedbackPlayerModal>` in `clip` mode (the boundary watcher
 * constrains playback to `[start, end]`). Clips have no review
 * tracking yet, so there's no Reviewed/New state here.
 *
 * Privacy: `clips[]` are already viewer-filtered server-side —
 * no client-side authorization.
 */

export type FeedbackClipsTabProps = {
  clips: CoachingClip[];
};

function clipDuration(c: CoachingClip): string {
  const dur = Math.max(0, Number(c.end_seconds) - Number(c.start_seconds));
  return formatPlayhead(dur);
}

export function FeedbackClipsTab({ clips }: FeedbackClipsTabProps) {
  const [openClip, setOpenClip] = useState<CoachingClip | null>(null);

  if (clips.length === 0) {
    return (
      <p
        className="text-ink-muted text-sm px-3 py-6"
        data-testid="feedback-clips-empty"
      >
        No coaching clips have been shared with you yet.
      </p>
    );
  }

  return (
    <>
      <ul
        className="grid gap-3"
        style={{ gridTemplateColumns: "repeat(auto-fill, minmax(360px, 1fr))" }}
        data-testid="feedback-clips-list"
      >
        {clips.map((clip) => (
          <ClipCard
            key={clip.id}
            clip={clip}
            onOpen={() => setOpenClip(clip)}
          />
        ))}
      </ul>

      {/* The M5 focused player — clip mode. The clip carries its
       * own match_id + team_id. */}
      {openClip ? (
        <FeedbackPlayerModal
          target={{ kind: "clip", clip: openClip }}
          match={{ id: openClip.match_id, team_id: openClip.team_id }}
          onClose={() => setOpenClip(null)}
        />
      ) : null}
    </>
  );
}

function ClipCard({
  clip,
  onOpen,
}: {
  clip: CoachingClip;
  onOpen: () => void;
}) {
  const title = clip.title?.trim() || "(untitled clip)";
  const meta = `${
    clip.match_id
  } · ${formatPlayhead(clip.start_seconds)}–${formatPlayhead(
    clip.end_seconds,
  )} (${clipDuration(clip)})`;

  return (
    <li>
      <article
        role="button"
        tabIndex={0}
        onClick={onOpen}
        onKeyDown={(e) => {
          if (e.key === "Enter" || e.key === " ") {
            e.preventDefault();
            onOpen();
          }
        }}
        data-testid="feedback-clip-card"
        aria-label={`Open ${title}`}
        className="flex w-full cursor-pointer items-center gap-3 rounded-lg border border-rule bg-bg-card px-3 py-2 text-left transition-colors hover:bg-surface focus:outline-none focus:ring-2 focus:ring-accent"
      >
        <FeedbackThumb kind="clip" id={clip.id} />
        <div className="min-w-0 flex-1">
          <span
            data-testid="feedback-clip-pill"
            className="rounded px-1.5 py-0.5 text-[0.6rem] font-display uppercase tracking-wider bg-surface text-ink-muted"
          >
            Clip · {clip.category || "other"}
          </span>
          <div className="font-display text-ink truncate mt-0.5">
            {title}
          </div>
          <div className="text-ink-muted text-xs font-mono mt-0.5 truncate">
            {meta}
          </div>
        </div>
      </article>
    </li>
  );
}
